import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Wine, ChevronRight, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/auto-translate";

export const Route = createFileRoute("/vouchers/")({
  head: () => ({ meta: [{ title: "İçecek Kuponlarım — JUËN" }] }),
  component: MyVouchersPage,
});

interface VoucherRow {
  id: string;
  code: string;
  created_at: string;
  redeemed_at: string | null;
  events: { id: string; name: string; starts_at: string } | null;
  drinks: { name: string } | null;
}

function MyVouchersPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [vouchers, setVouchers] = useState<VoucherRow[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const tc = useAutoTranslate(vouchers.flatMap((v) => [v.drinks?.name, v.events?.name]));

  useEffect(() => {
    if (!loading && !user) void navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data } = await supabase
        .from("drink_vouchers")
        .select("id,code,created_at,redeemed_at,events(id,name,starts_at),drinks(name)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      setVouchers((data as unknown as VoucherRow[]) ?? []);
      setPageLoading(false);
    })();
  }, [user]);

  // Group by event then by drink
  const grouped = useMemo(() => {
    const byEvent = new Map<
      string,
      {
        event: VoucherRow["events"];
        drinks: Map<string, { name: string; items: VoucherRow[] }>;
      }
    >();
    for (const v of vouchers) {
      const eventKey = v.events?.id ?? "unknown";
      if (!byEvent.has(eventKey)) {
        byEvent.set(eventKey, { event: v.events, drinks: new Map() });
      }
      const drinkName = tc(v.drinks?.name) ?? t("account.fallbackDrink");
      const bucket = byEvent.get(eventKey)!;
      if (!bucket.drinks.has(drinkName)) {
        bucket.drinks.set(drinkName, { name: drinkName, items: [] });
      }
      bucket.drinks.get(drinkName)!.items.push(v);
    }
    return Array.from(byEvent.values());
  }, [vouchers, t]);

  if (loading || !user || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/dashboard" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("account.title")}
        </Link>

        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("account.dashboard.vouchersTitle")}</h1>
        <p className="mt-2 text-muted-foreground">{t("account.vouchers.subtitle")}</p>

        {grouped.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-border p-10 text-center">
            <Wine className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">{t("account.vouchers.empty")}</p>
            <Button asChild className="mt-4">
              <Link to="/events">{t("account.browseEvents")}</Link>
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {grouped.map((group) => (
              <div key={group.event?.id ?? "unknown"} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <div className="mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  <h2 className="truncate text-lg font-semibold">{tc(group.event?.name) ?? t("account.fallbackEvent")}</h2>
                </div>
                <div className="space-y-2">
                  {Array.from(group.drinks.values()).map((d) => {
                    const remaining = d.items.filter((i) => !i.redeemed_at).length;
                    return (
                      <div key={d.name}>
                        <div className="mb-1.5 flex items-center justify-between">
                          <div className="text-sm font-medium">
                            {d.name}
                            <span className="ml-2 text-xs text-muted-foreground">
                              {t("account.vouchers.available", { remaining, total: d.items.length })}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          {d.items.map((v) => (
                            <Link
                              key={v.id}
                              to="/vouchers/$voucherId"
                              params={{ voucherId: v.id }}
                              className="group flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3 transition-all hover:-translate-y-0.5 hover:shadow-card-hover"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Wine className="h-4 w-4 shrink-0 text-primary" />
                                <span className="truncate font-mono text-xs text-muted-foreground">{v.code}</span>
                                {v.redeemed_at && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                    <CheckCircle2 className="h-3 w-3" /> {t("account.vouchers.used")}
                                  </span>
                                )}
                              </div>
                              <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
